import React, {useEffect, useRef, useState} from "react";
import styled from "styled-components";
import {Map, TileLayer} from "react-leaflet";
import L from 'leaflet';
import {GestureHandling} from "leaflet-gesture-handling";
import {MarkerCluster} from "./components/MarkerCluster";
import {getClosestMarkerOrDefault, getLatLangsFromMarkersData, MapBoxUrl} from "Helpers/MapHelper";
import media from "styled-media-query";
import breakpoints from "Helpers/BreakpointHelper";
import useBreakpoint from "Hooks/useBreakpoint";

export const Leaflet = ({
                          markersData,
                          FindUnitText,
                          zoom,
                          center,
                          height,
                          width,
                          borderradius,
                          selectedValue,
                          DirectionsLinkText,
                          nearestMarkers = [],
                          ActivateMapText,
                          OverlayImage,
                          handleOverlay,
                          YouAreHereText,
                          ShowLocationButton = false,
                          isVisible,
                        }) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [geoActivated, setGeoActivated] = useState(false);
  const mapRef = useRef();
  const domRef = useRef();
  const isMobile = useBreakpoint("mobile");
  
  L.Map.addInitHook("addHandler", "gestureHandling", GestureHandling);

  //TODO: se om möjlighet att sätta dependencies
  useEffect(() => {
    let map = mapRef.current;
    if (map != undefined && !nearestMarkers.length) {
      map = map.leafletElement;
      setBounds(map);

    }
  }, [mapRef?.current]);

  const setBounds = (map) => {
    let arrayOfLatLangs = getLatLangsFromMarkersData(markersData);
    arrayOfLatLangs.length ? map.fitBounds(arrayOfLatLangs, {maxZoom: 4}) : map.setView(center, zoom);
  };
  const handleGetLocation = (prevent = false) => {
    if (prevent) return false;
    const {current = {}} = mapRef;
    const {leafletElement: map} = current;
    if (map.locate()) {
      !geoActivated && setGeoActivated(!geoActivated);
    }
    map.on("locationfound", onLocationFound);
  };
  const onLocationFound = (e) => {
    let mark = getClosestMarkerOrDefault(e.latlng, markersData);
    flyToMarker(mark);
  };

  const flyToMarker = (mark) => {
    const {current = {}} = mapRef;
    const {leafletElement: map} = current;
    map.flyTo(mark, 12);
    map?.on("moveend", function () {
      setGeoActivated(false);
      setShowPrompt(false);
    });
  };


  return (
    <Wrapper>
      <MapContainer ref={domRef} borderradius={borderradius} height={height} width={width}>
        {!isVisible ? (
          <Overlay bgImage={OverlayImage} onClick={handleOverlay}>
            <OverlayTextWrapper>
              <OverlayText>{ActivateMapText}</OverlayText>
            </OverlayTextWrapper>
          </Overlay>
        ) : (
          <>
            <Map
              style={{
                height: "100%",
                width: "100%",
              }}
              center={center}
              zoom={zoom}
              ref={mapRef}
              maxZoom={20}
              gestureHandling={true}
            >
              <TileLayer url={MapBoxUrl} noWrap={true}/>

              <MarkerCluster
                {...{selectedValue}}
                markers={markersData}
                nearestMarkers={nearestMarkers}
                DirectionsLinkText={DirectionsLinkText}
                YouAreHereText={YouAreHereText}
              />
            </Map>
            <FindLocationCrosshairs onClick={() => {
              !showPrompt && !geoActivated && setShowPrompt(true);
              handleGetLocation();
            }} isActive={geoActivated}>
              <i className="fa-regular fa-location-crosshairs" aria-hidden="true"></i>
            </FindLocationCrosshairs>
            {showPrompt || !isMobile ? <FindMyLocationButton onClick={() => handleGetLocation(isMobile)}>
              {FindUnitText}
            </FindMyLocationButton> : null}
          </>
        )}
      </MapContainer>
    </Wrapper>
  );
};

const Wrapper = styled.div`
    display: flex;
    width: 100%;
    height: 100%;
    align-items: center;
    justify-content: center;
    position: relative;
`;

const Overlay = styled.div`
    height: 100%;
    width: 100%;
    cursor: pointer;
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    top: 0;
    background-size: cover;
    background-position: center;
    background-image: ${({bgImage}) => `url(${bgImage})`};
    border-radius: 8px;
`;
const OverlayText = styled.p`
    opacity: 0;
    transition: 0.2s ease;
    font-size: 24px;
`;
const OverlayTextWrapper = styled.div`
    width: 100%;
    height: 100%;
    transition: 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        ${OverlayText} {
            opacity: 1;
        }

        transition: 0.2s ease;
        background-color: rgba(255, 255, 255, 0.5);
    }
`;

const FindLocationCrosshairs = styled.button.attrs(({ariaLabel}) => ({
  "aria-label": ariaLabel || "find location button",
}))`
    display: none;
    ${media.lessThan(breakpoints.mobile)`
	display: flex;
	align-items: center;
	justify-content: center;
	position: absolute;
	background: ${({theme, isActive}) => !isActive ? theme.colors.white : theme.colors.primary};
	width: 34px;
	height: 34px;
	top: 15px;
	left: 10px;
	z-index: 400;
	border-radius: 3px;
	border: 2px solid #C0C0C2;
	i {
		font-size: 18px;
		color: ${({theme, isActive}) => !isActive ? theme.colors.black : theme.colors.white};
	}
	&:focus {
		outline: none;
	}
	`
    }
`;

const FindMyLocationButton = styled.button.attrs(({ariaLabel}) => ({
  "aria-label": ariaLabel || "find my location button",
}))`
    background-color: #fff;
    border: none;
    transition: 0.2s ease;
    line-height: 26px;
    display: block;
    box-shadow: 0 1px 5px rgba(0, 0, 0, 0.65);
    padding: 0.5rem 0.5rem;
    font-size: 16px;
    top: 12px;
    text-align: center;
    text-decoration: none;
    color: black;
    border-radius: 2px;
    cursor: pointer;
    position: absolute;
    left: 50px;
    z-index: 400;

    &:hover {
        transition: 0.2s ease;
        background-color: #f4f4f4;
    }
`;
export const MapContainer = styled.div`
    height: ${({height}) => (height ? height : "300px")};
    width: ${({width}) => (width ? width : "100%")};
    margin: 0 auto;

    .leaflet-popup-close-button {
        display: none !important;
    }

    ${media.lessThan(breakpoints.mobile)`
	    height: ${({smallHeight}) => (smallHeight ? smallHeight : "464px")};
		.leaflet-control {
			margin-top: 60px;
		}
		.leaflet-popup-close-button {
			display: none;
		}
	`}
    .leaflet-popup-content {
        width: auto !important;
        z-index: 400;
        margin: 0;
    }

    .leaflet-container {
        font: 14px/1.5 "Calibri";
        border-radius: ${({borderradius}) => (borderradius ? borderradius : "")};
    }

    .leaflet-popup-content-wrapper {
        border-radius: ${({borderradius}) => (borderradius ? borderradius : "")};
        padding: 0;
    }
`;
